import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Btn, Money, StatusBadge, Spinner } from "@/app/ui";
import { fmtDate } from "@/lib/format";
import { ArrowLeft, Mail, Phone, MapPin, Building2, Send } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const { data, loading, refetch } = useFetch(`/customers/${id}`, [id]);
  const [note, setNote] = useState("");

  if (loading || !data) return <Spinner />;
  const c = data.customer;

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/customers/${id}/notes`, { text: note }); setNote(""); refetch(); toast.success("Note added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up">
      <Link to="/app/customers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy mb-4"><ArrowLeft size={16} /> Back to customers</Link>
      <PageHeader title={c.name} description={c.company || "Customer profile"} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4">
          <div className="amden-card p-5 space-y-3">
            {c.company && <p className="flex items-center gap-2 text-slate-600 text-sm"><Building2 size={16} className="text-slate-400" /> {c.company}</p>}
            {c.email && <p className="flex items-center gap-2 text-slate-600 text-sm"><Mail size={16} className="text-slate-400" /> {c.email}</p>}
            {c.phone && <p className="flex items-center gap-2 text-slate-600 text-sm"><Phone size={16} className="text-slate-400" /> {c.phone}</p>}
            {c.address && <p className="flex items-center gap-2 text-slate-600 text-sm"><MapPin size={16} className="text-slate-400" /> {c.address}</p>}
          </div>
          <div className="amden-card p-5">
            <h3 className="font-semibold text-navy mb-3">Add a note</h3>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="customer-note-input" rows={3} placeholder="Log a call, meeting or update…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            <Btn variant="accent" className="mt-2 w-full" onClick={addNote} data-testid="customer-add-note"><Send size={15} /> Add note</Btn>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="amden-card p-4"><p className="text-xs uppercase text-slate-400 font-semibold">Quotes</p><p className="text-xl font-mono font-semibold text-navy mt-1">{data.quotes.length}</p></div>
            <div className="amden-card p-4"><p className="text-xs uppercase text-slate-400 font-semibold">Invoices</p><p className="text-xl font-mono font-semibold text-navy mt-1">{data.invoices.length}</p></div>
            <div className="amden-card p-4"><p className="text-xs uppercase text-slate-400 font-semibold">Jobs</p><p className="text-xl font-mono font-semibold text-navy mt-1">{data.jobs.length}</p></div>
          </div>

          {[["Quotes", data.quotes, "/app/quotes"], ["Invoices", data.invoices, "/app/invoices"]].map(([title, rows, link]) => (
            <div key={title} className="amden-card p-5">
              <h3 className="font-semibold text-navy mb-3">{title}</h3>
              {rows.length ? rows.map((r) => (
                <Link key={r.id} to={link} className="flex items-center justify-between py-2 border-t border-slate-100 first:border-0">
                  <span className="font-mono text-sm text-navy">{r.number}</span>
                  <span className="flex items-center gap-3"><Money value={r.total} symbol={sym} className="text-sm" /><StatusBadge status={r.status} /></span>
                </Link>
              )) : <p className="text-sm text-slate-400">None yet.</p>}
            </div>
          ))}

          <div className="amden-card p-5">
            <h3 className="font-semibold text-navy mb-3">Activity timeline</h3>
            <div className="space-y-4">
              {data.activities.map((a) => (
                <div key={a.id} className="flex gap-3">
                  <div className="flex flex-col items-center"><span className="h-2.5 w-2.5 rounded-full bg-blue-500 mt-1.5" /><span className="flex-1 w-px bg-slate-200" /></div>
                  <div className="pb-2">
                    <p className="text-sm text-navy">{a.text}</p>
                    <p className="text-xs text-slate-400">{a.actor} · {fmtDate(a.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
